import React, { useEffect, useState, useContext, useRef, useCallback } from 'react'
import ModalTest from '../../../BaseModal'
import { CloseDiv, ContainerOfModal, FormContainer, Header, HeaderTitle, InputDiv, Label, Input, ButtonContainer, Button } from '../../../styles/modal-style'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { X } from 'lucide-react'
import SelectSupplierInResource from './SelectSupplier'
import InfiniteSelect from '../../../InfiniteSelect'
import PriceManager, { PriceItem, PriceManagerRef } from '../../../PriceManager'
import ApiService from '../../../../services/ApiService'
import { FeedbackContext } from '../../../../contexts/FeedbackContext'

interface CompanyItemType {
  id: number
  companyName: string
  cnpj: string
}

interface CategoryItemType {
  id: number
  name: string
}

interface TypeItemType {
  id: number
  name: string
}

interface ResourceFormType {
  name: string
  code: string
  category: string
  type: string
  description: string
  prices: PriceItem[]
}

const CreateResourceModal = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const id = searchParams.get('id')
  const { setFeedback } = useContext(FeedbackContext)
  const priceManagerRef = useRef<PriceManagerRef>(null)
  const currentPricesRef = useRef<PriceItem[]>([])
  const [selectedSuppliers, setSelectedSuppliers] = useState<CompanyItemType[]>([])
  const [selectedCategories, setSelectedCategories] = useState<CategoryItemType[]>([])
  const [selectedTypes, setSelectedTypes] = useState<TypeItemType[]>([])
  const [previousCategoryId, setPreviousCategoryId] = useState<number | null>(null)
  const [formData, setFormData] = useState<ResourceFormType>({
    name: '',
    code: '',
    category: '',
    type: '',
    description: '',
    prices: [],
  })

  useEffect(() => {
    const fetchData = async () => {
    if (id) {
      try {
        const response = await ApiService.resources.findById(Number(id))
        const data = response.data
        
        const { suppliers, category, type, prices, code, description, companyResource, ...rest } = data
        
        setFormData({
          ...rest, 
          code: code || '',
          description: description || '',
          prices: prices || []
        })
        currentPricesRef.current = prices || []
        setSelectedSuppliers(suppliers)
        if (category) {
          setSelectedCategories([category])
        }
        if (type) {
          setSelectedTypes([type])
        }
        } catch (error) {
          console.log('Erro ao carregar dados:', error)
        }
      }
    }
    fetchData()
  }, [id])

  useEffect(() => {
    const currentCategoryId = selectedCategories[0]?.id || null
    
    if (selectedCategories.length === 0) {
      setSelectedTypes([])
      setPreviousCategoryId(null)
    } else if (currentCategoryId !== previousCategoryId && previousCategoryId !== null) {
      setSelectedTypes([])
      setPreviousCategoryId(currentCategoryId)
    } else if (previousCategoryId === null) {
      setPreviousCategoryId(currentCategoryId)
    }
  }, [selectedCategories, previousCategoryId])

  const handleInputChange = (field: keyof ResourceFormType, value: string | PriceItem[]) => {
    if (field === 'prices' && Array.isArray(value)) {
      currentPricesRef.current = value;
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      return
    }
    
    if (priceManagerRef.current) {
      const priceAdded = priceManagerRef.current.addPendingPrice();
      
      if (priceAdded) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }
    
    await new Promise(resolve => setTimeout(resolve, 50));
    
    try {
      const finalPrices = currentPricesRef.current.length > 0 ? currentPricesRef.current : formData.prices;
      
      const { id, companyResource, category, type, ...cleanFormData } = formData as any
      
      const selectedCategory = selectedCategories[0] || null
      const selectedType = selectedTypes[0] || null

      const requestData = {
        ...cleanFormData,
        prices: finalPrices.map((price: any) => ({
          price: Number(price.price),
          unitOfMeasurementId: Number(price.unitOfMeasurementId)
        })),
        suppliers: selectedSuppliers.map(supplier => supplier.id),
        categoryId: selectedCategory ? selectedCategory.id : null,
        category: selectedCategory ? selectedCategory.name : null,
        typeId: selectedType ? selectedType.id : null,
        type: selectedType ? selectedType.name : null
      }
      
      const action = id ? 'atualizar' : 'criar'
      
      if (id) {
        await ApiService.resources.update(Number(id), requestData)
      } else {
        await ApiService.resources.create(requestData)
      }

      setFeedback({ 
        message: `Insumo ${action} com sucesso!`, 
        isOpen: true, 
        statusOk: true 
      })
      
  navigate('/panel/stock/resource')
    } catch (error: any) {
      const action = id ? 'atualizar' : 'criar'
      let errorMessage = `Erro ao ${action} insumo`

      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.response?.status === 400) {
        errorMessage = 'Dados inválidos. Verifique as informações inseridas.'
      } else if (error.response?.status === 500) {
        errorMessage = 'Erro interno do servidor. Tente novamente.'
      } else if (error.message) {
        errorMessage = error.message
      }

      setFeedback({ 
        message: errorMessage, 
        isOpen: true, 
        statusOk: false 
      })
    }
  }
  
  return (
    <ModalTest>
      <ContainerOfModal width='850px'>
        <Header>
          <HeaderTitle>{id ? 'Editar insumo' : 'Novo insumo'}</HeaderTitle>
          <CloseDiv onClick={() => navigate('/panel/stock/resource')}> <X size={20} /></CloseDiv>
        </Header>
        <FormContainer onSubmit={onSubmit} columns='1fr 1fr'>
          <InputDiv>
            <Label>Nome</Label>
            <Input 
              autoComplete='off' 
              type='text' 
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="Digite o nome do insumo"
              style={{ height: '40px' }}
            />
          </InputDiv>

          <InputDiv>
            <Label>Categoria</Label>
            <InfiniteSelect
              mode="crud"
              value={selectedCategories[0] || null}
              onSelect={(option) => setSelectedCategories(option ? [option] : [])}
              endpointConfig={{
                listPath: '/resources/resource/util/categories',
                createPath: '/resources/resource/util/categories',
                deletePath: '/resources/resource/util/categories',
                headers: { 'Cache-Control': 'no-cache' }
              }}
              onItemCreate={async (name: string) => {
                const response = await ApiService.resources.createCategory({ name });
                return response.data;
              }}
              onItemDelete={async (id: any) => {
                await ApiService.resources.deleteCategory(id);
              }}
              fetchFunction={async (limit: number, offset: number, search?: string) => {
                const response = await ApiService.resources.getCategories({
                  headers: { 'Cache-Control': 'no-cache' }
                });
                return response.data;
              }}
              placeholder="Digite para buscar ou criar categoria"
              initialOptions={selectedCategories}
              allowCreate={true}
              allowDelete={true}
              showCreateOnlyIfNotExists={true}
              minCreateLength={2}
            />
          </InputDiv>

          <InputDiv>
            <Label>Tipo</Label>
            <InfiniteSelect
              mode="crud"
              value={selectedTypes[0] || null}
              onSelect={(option) => setSelectedTypes(option ? [option] : [])}
              endpointConfig={{
                listPath: '/resources/resource/util/types',
                createPath: '/resources/resource/util/types',
                deletePath: '/resources/resource/util/types',
                headers: { 'Cache-Control': 'no-cache' }
              }}
              onItemCreate={async (name: string) => {
                const response = await ApiService.resources.createType({ 
                  name,
                  categoryId: selectedCategories[0]?.id
                });
                return response.data;
              }}
              onItemDelete={async (id: any) => {
                await ApiService.resources.deleteType(id);
              }}
              fetchFunction={async (limit: number, offset: number, search?: string) => {
                const response = await ApiService.resources.getTypes({
                  params: {
                    categoryId: selectedCategories[0]?.id
                  },
                  headers: { 'Cache-Control': 'no-cache' }
                });
                return response.data;
              }}
              placeholder={selectedCategories.length > 0 ? "Digite para buscar ou criar tipo" : "Primeiro selecione uma categoria"}
              disabled={selectedCategories.length === 0}
              initialOptions={selectedTypes}
              allowCreate={true}
              allowDelete={true}
              showCreateOnlyIfNotExists={true}
              minCreateLength={2}
            />
          </InputDiv>

          <InputDiv>
            <Label>Código</Label>
            <Input 
              autoComplete='off' 
              type='text' 
              value={formData.code}
              onChange={(e) => handleInputChange('code', e.target.value)}
              placeholder="Digite aqui"
              style={{ height: '40px' }}
            />
          </InputDiv>

          <SelectSupplierInResource suppliers={selectedSuppliers} setSuppliers={setSelectedSuppliers} />

          <PriceManager
            ref={priceManagerRef}
            prices={formData.prices}
            onChange={(prices) => handleInputChange('prices', prices)}
            baseUrl={`${process.env.REACT_APP_GATEWAY_BASE_URL}/resources` || ''}
            label="Preços"
          />

          <InputDiv style={{ gridColumn: '1 / -1' }}>
            <Label>Descrição</Label>
            <Input 
              autoComplete='off' 
              type='text' 
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Digite a descrição do insumo"
              style={{ height: '40px' }}
            />
          </InputDiv>

          <ButtonContainer>
            <Button 
              type='button' 
              onClick={() => navigate('/panel/stock/resource')}
              className='cancel'
            >
              Cancelar
            </Button>
            <Button type='submit'>{id ? 'Alterar' : '+ Adicionar'} insumo</Button>
          </ButtonContainer>
        </FormContainer>
      </ContainerOfModal>
    </ModalTest>
  )
}

export default CreateResourceModal